import { PPPC_SERVICE_GROUPS, PppcChoicesMap, PppcServiceKey } from "@/types/pppc";
import {
  validateIdentifier,
  WIZARD_IDENTIFIER_MAX,
} from "./wizardValidation";

/** Built-in TCC keys we ship — anything else is treated as a "custom" key. */
const KNOWN_KEYS = new Set<string>(
  PPPC_SERVICE_GROUPS.flatMap((g) => g.services.map((s) => s.key as string)),
);

export interface PppcValidationIssue {
  /** The offending TCC key. */
  key: string;
  /** Human-readable explanation suitable for display in the UI. */
  message: string;
}

export class PppcValidationError extends Error {
  readonly issues: PppcValidationIssue[];
  constructor(issues: PppcValidationIssue[]) {
    const summary =
      issues.length === 1
        ? issues[0].message
        : `${issues.length} invalid privacy services: ${issues
            .map((i) => i.key)
            .join(", ")}`;
    super(summary);
    this.name = "PppcValidationError";
    this.issues = issues;
  }
}

/**
 * Validate the set of TCC service keys carried in a PPPC policy. Built-in
 * keys are trusted; custom keys must match Apple's identifier shape and avoid
 * obviously malicious characters. Returns the list of issues (empty if OK).
 *
 * Used as a server-side validation step inside savePppcPolicy so that the
 * same rules apply whether the call originates from the wizard, the dialog,
 * or the activity-log "Revert" action.
 */
export function validatePppcChoices(
  choices: PppcChoicesMap,
): PppcValidationIssue[] {
  const issues: PppcValidationIssue[] = [];
  for (const rawKey of Object.keys(choices)) {
    const v = choices[rawKey as PppcServiceKey];
    if (v !== "allow" && v !== "deny") continue;
    if (KNOWN_KEYS.has(rawKey)) continue;

    // Delegate to the shared identifier validator so PPPC custom keys and
    // App Config keys share one contract (start with letter, max 64,
    // forbidden chars). The PPPC surface re-wraps the message to keep its
    // historical "Custom TCC key …" prefix.
    const err = validateIdentifier(rawKey, { treatEmptyAsError: true });
    if (!err) continue;

    if (rawKey.length === 0) {
      issues.push({ key: rawKey, message: "Custom TCC key cannot be empty." });
      continue;
    }
    if (rawKey.length > WIZARD_IDENTIFIER_MAX) {
      issues.push({
        key: rawKey,
        message: `Custom TCC key "${rawKey}" is too long (max ${WIZARD_IDENTIFIER_MAX} characters).`,
      });
      continue;
    }
    issues.push({
      key: rawKey,
      message: `Custom TCC key "${rawKey}" ${err.replace(/^Key /, "")}`,
    });
  }
  return issues;
}

/** Throw a {@link PppcValidationError} if any issues are found. */
export function assertValidPppcChoices(choices: PppcChoicesMap): void {
  const issues = validatePppcChoices(choices);
  if (issues.length > 0) throw new PppcValidationError(issues);
}
